import type * as React from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
  Link as AriaLink,
  type LinkProps as AriaLinkProps,
  type SlotProps,
} from "react-aria-components";
import { icon } from "../icon.css.ts";
import { button, buttonContent, buttonText } from "./Button.css.ts";
import { Spinner as DesignSystemSpinner } from "./Spinner.tsx";

export type Hierarchy = keyof typeof button.classNames.variants.hierarchy;

export type Size = keyof typeof button.classNames.variants.size;

const iconSizeMapping: Record<
  Size,
  keyof typeof icon.classNames.variants.size
> = {
  xs: "s",
  s: "s",
  m: "m",
};

interface BaseProps extends SlotProps {
  /** Visual style variant. */
  hierarchy?: Hierarchy;
  /** Button size. */
  size?: Size;
  /** Text content of the button. */
  children?: string | number | readonly (string | number)[];
  /** Icon displayed before the button text. */
  leadingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Icon displayed after the button text. */
  trailingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Apply destructive (red) styling for dangerous actions. */
  destructive?: boolean;
  /** Show loading spinner. */
  loading?: boolean;
  /** Disable the button. */
  disabled?: boolean;
  /** Allow button to grow to fill available space. */
  grow?: boolean;
}

interface BaseButtonProps
  extends BaseProps, Pick<AriaButtonProps, "onPress" | "type"> {
  /** Ref to the underlying button element. */
  buttonRef?: React.Ref<HTMLButtonElement>;

  anchorRef?: never;
  href?: never;
  target?: never;
  download?: never;
  /** Use outline style instead of filled. */
  outline?: boolean;
}

interface Default extends BaseButtonProps {
  /** Button variant type. */
  variant?: "default";
  outline?: never;
  ariaLabel?: string;
}

interface IconOnly extends BaseButtonProps {
  variant: "iconOnly";
  children?: never;
  leadingIcon: React.FC<React.SVGProps<SVGSVGElement>>;
  trailingIcon?: never;
  loading?: false;
  outline?: boolean;
  grow?: never;
  ariaLabel?: string;
}

interface IconOnlyLoading extends BaseButtonProps {
  variant: "iconOnly";
  children?: never;
  leadingIcon?: never;
  trailingIcon?: never;
  loading: true;
  outline?: never;
  ariaLabel?: string;
}

interface LinkButton
  extends
    BaseProps,
    Pick<AriaLinkProps, "target" | "download" | "onPress" | "routerOptions"> {
  /** Button variant type. */
  variant: "link";
  /** URL to navigate to when clicked. */
  href: string;
  /** Ref to the underlying anchor element. */
  anchorRef?: React.Ref<HTMLAnchorElement>;
  /** Ref to the underlying anchor element. */
  ref?: React.Ref<HTMLAnchorElement>;

  buttonRef?: never;
  loading?: never;
  outline?: never;
}

interface IconOnlyLink
  extends
    BaseProps,
    Pick<AriaLinkProps, "target" | "download" | "onPress" | "routerOptions"> {
  /** Button variant type. */
  variant: "iconOnlyLink";
  /** URL to navigate to when clicked. */
  href: string;
  /** Icon to display. */
  leadingIcon: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Ref to the underlying anchor element. */
  anchorRef?: React.Ref<HTMLAnchorElement>;
  /** Ref to the underlying anchor element. */
  ref?: React.Ref<HTMLAnchorElement>;

  children?: never;
  trailingIcon?: never;
  buttonRef?: never;
  destructive?: never;
  loading?: never;
  outline?: never;
  grow?: never;
  ariaLabel?: string;
}

export type Props =
  Default | IconOnly | IconOnlyLoading | LinkButton | IconOnlyLink;

type WithDefaults<T extends BaseProps> = T &
  Required<Pick<T, "hierarchy" | "size">>;
function populateDefaults<T extends BaseProps>(props: T): WithDefaults<T> {
  const {
    hierarchy = "primary",
    size = "m",
    destructive = false,
    loading = false,
    grow = true,
    ...rest
  } = props;

  return {
    hierarchy,
    size,
    destructive,
    loading,
    grow,
    ...rest,
  } as WithDefaults<T>;
}

/**
 * A versatile button component supporting multiple variants, icons, and loading states.
 * Can render as a button or link element depending on the variant.
 */
export const Button: React.FC<Props> = ({ children, ...unsafeProps }) => {
  const props = populateDefaults(unsafeProps);
  const {
    hierarchy,
    size,
    destructive,
    loading,
    disabled,
    variant,
    outline = false,
    grow,
  } = props;

  if (props.variant === "link") {
    const { anchorRef, ref, ...rest } = props;
    return (
      <Link ref={ref ?? anchorRef} {...rest}>
        {children}
      </Link>
    );
  }

  if (props.variant === "iconOnlyLink") {
    const { anchorRef, ref, leadingIcon, ariaLabel, ...rest } = props;
    return (
      <IconLink
        ref={ref ?? anchorRef}
        icon={leadingIcon}
        aria-label={ariaLabel}
        {...rest}
      />
    );
  }

  const wrapperClassname = button({
    size,
    hierarchy,
    iconOnly: variant === "iconOnly",
    loading,
    outline,
    grow: variant === "iconOnly" ? false : grow,
    destructive,
  });

  const { type, onPress, buttonRef, ariaLabel, ...restProps } = props;

  if (variant === "iconOnly" && loading === true) {
    return (
      <AriaButton
        className={wrapperClassname}
        type={type}
        onPress={onPress}
        ref={buttonRef}
        isDisabled={disabled}
        isPending={true}
        aria-label={ariaLabel}
        {...restProps}
      >
        <Spinner size={size} hierarchy={hierarchy} destructive={destructive} />
      </AriaButton>
    );
  }

  if (variant === "iconOnly") {
    const Icon = props.leadingIcon;

    return (
      <AriaButton
        className={wrapperClassname}
        type={type}
        onPress={onPress}
        isDisabled={disabled}
        ref={buttonRef}
        aria-label={ariaLabel}
        {...restProps}
      >
        <Icon className={icon({ size: iconSizeMapping[size] })} />
      </AriaButton>
    );
  }

  // TODO: since we currently only animate `flex-grow`, adding or removing icons (including loading state) will not
  //  animate. Maybe just slide in out, and let the wrapper jump? Not sure.
  return (
    <AriaButton
      className={wrapperClassname}
      type={type}
      onPress={onPress}
      ref={buttonRef}
      isDisabled={disabled}
      isPending={loading}
      {...restProps}
    >
      <ButtonContent {...props}>{children}</ButtonContent>
      {loading === true && (
        <Spinner size={size} hierarchy={hierarchy} destructive={destructive} />
      )}
    </AriaButton>
  );
};

const Link: React.FC<LinkButton & { ref?: React.Ref<HTMLAnchorElement> }> = ({
  children,
  ref,
  ...props
}) => {
  const { hierarchy, size, disabled, destructive, grow } = props;
  const wrapperClassname = button({ size, hierarchy, destructive, grow });

  return (
    <AriaLink
      className={wrapperClassname}
      ref={ref}
      isDisabled={disabled}
      {...props}
    >
      <ButtonContent {...props}>{children}</ButtonContent>
    </AriaLink>
  );
};

const IconLink: React.FC<
  Omit<IconOnlyLink, "leadingIcon"> & {
    ref?: React.Ref<HTMLAnchorElement>;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }
> = ({ ref, icon: LinkIcon, ...props }) => {
  const { hierarchy, size = "m", disabled } = props;
  const wrapperClassname = button({
    size,
    hierarchy,
    iconOnly: true,
    grow: false,
  });

  return (
    <AriaLink
      className={wrapperClassname}
      ref={ref}
      isDisabled={disabled}
      {...props}
    >
      <LinkIcon className={icon({ size: iconSizeMapping[size] })} />
    </AriaLink>
  );
};

function ButtonContent(props: BaseProps) {
  const { leadingIcon: LeadingIcon, trailingIcon: TrailingIcon } = props;
  const { size = "m", hierarchy, destructive, loading, children } = props;

  return (
    <div className={buttonContent({ loading, hierarchy, destructive })}>
      {LeadingIcon && (
        <LeadingIcon className={icon({ size: iconSizeMapping[size] })} />
      )}
      {children != null && (
        <span className={buttonText({ size })}>{children}</span>
      )}
      {TrailingIcon && (
        <TrailingIcon className={icon({ size: iconSizeMapping[size] })} />
      )}
    </div>
  );
}

function getSpinnerColor(
  hierarchy: Hierarchy | undefined,
  destructive: boolean | undefined,
) {
  if (!hierarchy) {
    return destructive === true ? "destructive" : "white";
  }

  if (destructive === true) {
    switch (hierarchy) {
      case "primary": {
        return "white";
      }
      default: {
        return "destructive";
      }
    }
  }

  switch (hierarchy) {
    case "primary":
    case "alertWarningPrimary": {
      return "white";
    }
    case "alertErrorSecondary":
    case "alertWarningSecondary": {
      return "alertWarning";
    }
    case "alertSuccessSecondary": {
      return "alertSuccess";
    }
    default: {
      return "royalBlue";
    }
  }
}

const Spinner: React.FC<{
  size: Size;
  hierarchy?: Hierarchy;
  destructive?: boolean;
}> = ({ size, hierarchy, destructive }) => (
  <DesignSystemSpinner
    size={size}
    color={getSpinnerColor(hierarchy, destructive)}
  />
);
